import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Anchor, Check, Loader2, ArrowUpRight, Download } from "lucide-react";
import { API, LOGO } from "@/lib/site";
import { COLLECTIVE, MEMBER_FLEET } from "@/lib/collective";
import Navbar from "@/components/site/Navbar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const inputCls =
  "bg-white/[0.03] border-white/15 rounded-none h-12 text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-gold focus-visible:border-gold";

const fade = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

function ClaimForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [entry, setEntry] = useState(null);
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/waitlist/count`)
      .then(({ data }) => setCount(data))
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, form);
      setEntry(data);
      setDone(true);
      setCount((c) => (c ? { ...c, claimed: c.claimed + 1, remaining: Math.max(0, c.remaining - 1) } : c));
      toast.success("Your place is noted. Welcome aboard.");
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
      <div className="lg:col-span-5">
        <p className="text-xs uppercase tracking-mega text-gold">The Founding Fleet</p>
        <h2 className="mt-6 font-serif font-light text-5xl md:text-7xl text-white leading-[0.9] tracking-tight">
          #001 <span className="text-white/30">—</span> #100
        </h2>
        <p className="mt-8 max-w-md text-base md:text-lg font-light text-white/60 leading-relaxed">
          The original hundred. Numbered, permanent, and impossible to buy your way into once they're gone.
        </p>
        <ul className="mt-10 space-y-3">
          {COLLECTIVE.foundingBenefits.map((b) => (
            <li key={b} className="flex items-start gap-3 text-white/80">
              <Anchor size={15} className="text-gold shrink-0 mt-1" />
              <span className="text-sm">{b}</span>
            </li>
          ))}
        </ul>
        {count && (
          <div className="mt-10 border border-gold/30 p-5" data-testid="founding-counter">
            <div className="flex items-end justify-between">
              <span className="font-serif text-4xl text-gold">{count.claimed}</span>
              <span className="text-xs uppercase tracking-[0.2em] text-white/40">of {count.total} claimed</span>
            </div>
            <div className="mt-3 h-1 w-full bg-white/10">
              <div className="h-full bg-gold transition-all duration-700" style={{ width: `${(count.claimed / count.total) * 100}%` }} />
            </div>
            <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-gold/70">
              Once they're claimed, they're gone.
            </p>
          </div>
        )}
      </div>

      <div className="lg:col-span-7 w-full">
        {done ? (
          <div className="flex flex-col items-center justify-center text-center border border-gold/30 bg-white/[0.02] py-24 px-8" data-testid="collective-success">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-gold text-gold mb-6">
              <Check size={28} />
            </div>
            {entry?.founder_number ? (
              <>
                <p className="text-xs uppercase tracking-[0.25em] text-gold">Founding Fleet</p>
                <h3 className="mt-3 font-serif text-6xl md:text-8xl text-gold-gradient leading-none" data-testid="founder-number">
                  #{String(entry.founder_number).padStart(3, "0")}
                </h3>
                <p className="mt-5 max-w-sm text-white/60 font-light">
                  Your permanent number is reserved. We'll reach out as the Founding Fleet opens — welcome aboard.
                </p>
                <a
                  href={`${API}/waitlist/${entry.id}/certificate`}
                  className="mt-8 inline-flex items-center gap-2 bg-gold text-black px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-gold-light transition-colors"
                  data-testid="founder-certificate-download"
                >
                  <Download size={14} /> Download your certificate
                </a>
              </>
            ) : (
              <>
                <h3 className="font-serif text-3xl md:text-4xl text-white">You're on the list.</h3>
                <p className="mt-4 max-w-sm text-white/60 font-light">
                  The founding hundred are spoken for — you're on the Collective waitlist. We'll reach out as new levels open.
                </p>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="border border-white/10 bg-white/[0.02] p-6 md:p-10 space-y-6" data-testid="collective-form">
            <div>
              <p className="font-serif text-2xl md:text-3xl text-white">Claim your place</p>
              <p className="mt-2 text-sm text-white/50">Register interest in the Founding Fleet. No commitment — just first in line.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-white/50">Full Name *</label>
                <Input className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Harbor" data-testid="collective-name" />
              </div>
              <div>
                <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-white/50">Phone</label>
                <Input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" data-testid="collective-phone" />
              </div>
            </div>
            <div>
              <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-white/50">Email *</label>
              <Input className={inputCls} type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="collective-email" />
            </div>
            <div>
              <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-white/50">Your machine (optional)</label>
              <Textarea className={`${inputCls} h-24 py-3`} value={form.message} onChange={set("message")} placeholder="Tell us what you own — boat, car, anything worth protecting." data-testid="collective-message" />
            </div>
            <button type="submit" disabled={loading} className="inline-flex items-center gap-2 bg-gold text-black px-12 py-4 text-xs uppercase tracking-[0.25em] hover:bg-gold-light transition-colors duration-300 disabled:opacity-60" data-testid="collective-submit">
              {loading ? <><Loader2 size={16} className="animate-spin" /> Sending</> : "Request an invitation"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Collective() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.65, 0.95]);
  const c = COLLECTIVE;

  return (
    <div className="bg-black">
      <Navbar onNavigate={() => {}} />

      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] w-full overflow-hidden" data-testid="collective-hero">
        <motion.div style={{ y }} className="absolute inset-0 will-change-transform">
          <img src={c.hero.image} alt="Black Anchor Collective" className="h-full w-full object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-black" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60" />
        <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-center px-5 md:px-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }} className="mb-6 flex items-center gap-3 text-[11px] md:text-xs uppercase tracking-mega text-gold">
            <span className="h-px w-10 bg-gold/60" /> {c.hero.kicker}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }} className="font-serif font-light leading-[0.86] text-white tracking-tighter text-6xl sm:text-7xl md:text-8xl lg:text-[9rem]">
            {c.hero.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1 }} className="mt-8 font-serif italic text-2xl md:text-4xl text-gold-gradient">
            {c.hero.tagline}
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1 }} className="mt-6 max-w-xl text-base md:text-lg font-light text-white/70 leading-relaxed">
            {c.hero.intro}
          </motion.p>
        </div>
      </section>

      {/* CHAPTERS */}
      <section className="relative bg-black py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          {c.chapters.map((ch) => (
            <motion.div key={ch.num} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fade} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 border-t border-white/10 py-12 md:py-16">
              <div className="md:col-span-3"><span className="font-serif text-6xl md:text-8xl text-white/10 leading-none">{ch.num}</span></div>
              <h3 className="md:col-span-4 font-serif text-3xl md:text-4xl text-white font-light leading-tight">{ch.title}</h3>
              <p className="md:col-span-5 text-base md:text-lg font-light text-white/60 leading-relaxed">{ch.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* MEMBER FLEET FEATURE */}
      <section className="relative bg-[#0A0A0A] py-24 md:py-32 border-t border-white/10" data-testid="member-fleet">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div initial={{ opacity: 0, clipPath: "inset(12% 12% 12% 12%)" }} whileInView={{ opacity: 1, clipPath: "inset(0% 0% 0% 0%)" }} viewport={{ once: true }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} className="relative aspect-[4/3] overflow-hidden border border-white/10">
            <img src={c.memberFleet.image} alt="Member fleet feature" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-0 left-0 p-6">
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold">{c.memberFleet.label}</p>
              <p className="mt-2 font-serif text-3xl text-white">{c.memberFleet.machine}</p>
              <p className="text-sm text-white/60">{c.memberFleet.spec}</p>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}>
            <p className="mb-6 text-xs uppercase tracking-mega text-gold">The Member Fleet</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl text-white tracking-tight leading-[0.95]">
              Your machine becomes the brand
            </h2>
            <p className="mt-6 max-w-lg text-base md:text-lg font-light text-white/60 leading-relaxed">
              Cinematic photography, owner profiles, build features and transformations. A curated fleet
              of member boats and cars — the faces and machines of Black Anchor.
            </p>
          </motion.div>
        </div>
      </section>

      {/* MEMBER FLEET GALLERY */}
      <section className="relative bg-[#0A0A0A] pb-24 md:pb-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {MEMBER_FLEET.map((m, i) => (
              <motion.div
                key={m.number}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
                className="group relative aspect-[4/5] overflow-hidden border border-white/10"
                data-testid={`member-card-${m.number}`}
              >
                <img src={m.image} alt={m.machine} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                <span className="absolute top-5 left-5 font-serif text-5xl text-gold/90">#{m.number}</span>
                <div className="absolute bottom-0 left-0 p-6">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Member #{m.number}</p>
                  <p className="mt-2 font-serif text-2xl text-white">{m.machine}</p>
                  <p className="text-sm text-white/60">{m.spec}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="mt-6 text-center text-xs uppercase tracking-[0.2em] text-white/30">
            A glimpse of the fleet — member features coming soon.
          </p>
        </div>
      </section>

      {/* EXPERIENCES */}
      <section className="relative bg-black py-24 md:py-32 border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }} className="mb-14 max-w-2xl">
            <p className="mb-6 text-xs uppercase tracking-mega text-gold">The Experiences</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl text-white tracking-tight leading-[0.95]">More than a discount</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {c.experiences.map((ex, i) => (
              <motion.div key={ex.name} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.08 }} className="bg-black p-8 md:p-10">
                <p className="font-serif text-2xl md:text-3xl text-white">{ex.name}</p>
                <p className="mt-3 text-sm font-light text-white/55 leading-relaxed">{ex.note}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ECOSYSTEM */}
      <section className="relative bg-[#0A0A0A] py-24 md:py-32 border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }} className="mb-12 max-w-2xl">
            <p className="mb-6 text-xs uppercase tracking-mega text-gold">The Ecosystem</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl text-white tracking-tight leading-[0.95]">One anchor. Every asset.</h2>
            <p className="mt-6 text-base md:text-lg font-light text-white/60 leading-relaxed">
              Boat, exotic, RV — all under one standard. The Collective is built to grow with everything
              worth protecting.
            </p>
          </motion.div>
          <div className="flex flex-col border-t border-white/10">
            {c.ecosystem.map((e, i) => (
              <motion.div key={e} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.06 }} className="group flex items-center justify-between border-b border-white/10 py-6 md:py-8">
                <span className="font-serif font-light text-4xl md:text-6xl text-white/80 group-hover:text-gold transition-colors duration-300">{e}</span>
                <Anchor size={20} className="text-white/20 group-hover:text-gold transition-colors duration-300" />
              </motion.div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
            {c.subBrands.map((s) => (
              <span key={s} className="text-xs uppercase tracking-[0.2em] text-white/40">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* BEYOND */}
      <section className="relative bg-black py-24 md:py-32 border-t border-white/10">
        <div className="mx-auto max-w-3xl px-5 md:px-10 text-center">
          <p className="mb-6 text-xs uppercase tracking-mega text-gold">Beyond the Fleet</p>
          <p className="font-serif font-light text-2xl md:text-4xl text-white leading-relaxed">
            {c.beyond}
          </p>
        </div>
      </section>

      {/* CLAIM */}
      <section id="claim" className="relative bg-[#0A0A0A] py-24 md:py-32 border-t border-white/10" data-testid="collective-claim">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <ClaimForm />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-black border-t border-white/10 py-12">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3 group">
            <img src={LOGO} alt="Black Anchor Collective" className="h-11 w-11 object-contain" />
            <span className="font-serif tracking-[0.25em] text-white/80 group-hover:text-gold transition-colors">BLACK ANCHOR COLLECTIVE</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-gold transition-colors" data-testid="collective-back">
            Back to site <ArrowUpRight size={14} />
          </Link>
        </div>
      </footer>
    </div>
  );
}
